import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import styled, { useTheme } from 'styled-components'

import { cardFrameCSS } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { ToggleSwitch } from '@/modules/shared/components/toggle-switch/ToggleSwitch'

import type { ComponentPropsWithoutRef, ReactNode } from 'react'

type WaitlistSectionProps = Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> & {
  checked: boolean
  onChange: () => void
  /** Why the request is worth making. Differs between booking and an existing appointment. */
  description: ReactNode
  /** Shown under the toggle once the request is on. Omit it and nothing is shown. */
  note?: ReactNode
  /** Distinguishes the switch from any other on the page. */
  toggleId: string
  disabled?: boolean
}

/**
 * The "call me if something comes free" opt-in, heading and all.
 *
 * A plain component rather than a compound: both screens compose the same
 * parts in the same order and only the wording differs, which is the case
 * `docs/frontend/frontend-architecture.md` says to solve with props. The heading and the
 * switch label live here because they are identical on both, and the label is
 * also the switch's accessible name — keeping it in one place stops the two
 * drifting apart.
 *
 * KNOWN DEVIATION, raised in review on AZMM-2699 and accepted for the demo
 * deadline: `StNote` is a one-off panel with no counterpart in the kit. Debt is
 * attached to it. Treat it as chrome that has to be brought back in line, not
 * as a pattern to copy.
 */
const WaitlistSection = ({
  checked,
  onChange,
  description,
  note,
  toggleId,
  disabled,
  ...props
}: WaitlistSectionProps) => {
  const theme = useTheme()

  const label = t({
    id: 'appointments.waitlist.title',
    comment: 'Label of the waitlist opt-in toggle, also its accessible name',
    message: 'Verwittig me bij een vroegere plek',
  })

  return (
    <FlexColumn gap={theme.UI.SpacingPx.Space3} {...props}>
      <Span type='BodyMediumBold'>
        {t({
          id: 'appointments.waitlist.heading',
          comment: 'Section heading above the waitlist opt-in',
          message: 'Vroeger geholpen worden?',
        })}
      </Span>

      <StCard>
        <FlexRow alignItems='center' justifyContent='space-between' gap={theme.UI.SpacingPx.Space4}>
          <Span type='BodyMediumBold'>{label}</Span>
          <ToggleSwitch id={toggleId} checked={checked} onChange={onChange} disabled={disabled} aria-label={label} />
        </FlexRow>

        <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
          {description}
        </Span>

        {note ? (
          <StNote>
            <StNoteIcon icon={['fal', 'clock']} />
            <FlexColumn gap={theme.UI.SpacingPx.Space1}>{note}</FlexColumn>
          </StNote>
        ) : null}
      </StCard>
    </FlexColumn>
  )
}

export { WaitlistSection }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StCard = styled.div`
  ${cardFrameCSS()}

  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.UI.SpacingPx.Space3};
  `}
`

const StNote = styled.div`
  ${({ theme }) => `
    display: flex;
    gap: ${theme.UI.SpacingPx.Space3};
    padding: ${theme.UI.SpacingPx.Space4};
    border-radius: ${theme.UI.BorderRadius.Small}px;
    background-color: ${theme.Colors.Neutrals.Lighter};
  `}
`

const StNoteIcon = styled(FontAwesomeIcon)`
  /* Holds the icon on the first line of the note rather than centring it against wrapped text. */
  flex-shrink: 0;
  margin-top: 2px;
  color: ${({ theme }) => theme.Colors.Theme.Primary};
`
